import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Heart,
  Target,
  Zap,
  TrendingUp,
  Ear,
  BookOpenCheck,
  Search,
  MessageCircle,
  MapPin,
  ArrowRight,
} from "lucide-react";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Integrity",
    text: "We value honest, responsible and transparent professional communication.",
  },
  {
    icon: Heart,
    title: "Respect",
    text: "We treat employers and candidates with professionalism throughout every interaction.",
  },
  {
    icon: Target,
    title: "Relevance",
    text: "We focus on alignment between role requirements and candidate capabilities.",
  },
  {
    icon: Zap,
    title: "Responsiveness",
    text: "We believe timely coordination is essential to a positive recruitment experience.",
  },
  {
    icon: TrendingUp,
    title: "Growth",
    text: "We support connections that contribute to long-term professional and organizational progress.",
  },
];

const APPROACH = [
  {
    icon: Ear,
    step: "01",
    title: "Listen Carefully",
    text: "We start by understanding what you truly need — team, culture and priorities.",
  },
  {
    icon: BookOpenCheck,
    step: "02",
    title: "Understand Requirements",
    text: "We translate role expectations into clear, structured search parameters.",
  },
  {
    icon: Search,
    step: "03",
    title: "Search Purposefully",
    text: "We identify and shortlist candidates based on relevance and suitability.",
  },
  {
    icon: MessageCircle,
    step: "04",
    title: "Communicate Clearly",
    text: "We keep everyone informed with timely, respectful updates through each stage.",
  },
];

export default function AboutPage() {
  return (
    <div className="flex flex-col">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-soft">
        {/* MOBILE (< lg): stacked — full image on top, text below on soft bg */}
        <div className="lg:hidden">
          <div className="relative w-full aspect-[4/3] bg-brand-soft overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?auto=format&fit=crop&w=1600&q=85"
              alt="Diverse professional team in a modern Indian office"
              className="absolute inset-0 w-full h-full object-contain"
              style={{ objectPosition: "center center" }}
              loading="eager"
              draggable={false}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-brand-soft" />
          </div>
          <div className="container-brand relative z-10 py-10 sm:py-12">
            <span className="eyebrow">About Ascend Tech Solution</span>
            <h1 className="mt-4 heading-1">
              People-First Recruitment for Modern Organizations
            </h1>
            <p className="mt-5 body-lg">
              Based in Pune, Maharashtra, Ascend Tech Solution supports
              organizations and professionals through focused recruitment, talent
              sourcing and hiring coordination across IT and Non-IT functions.
            </p>
          </div>
        </div>

        {/* DESKTOP (lg+): overlay hero with faded image behind */}
        <div className="hidden lg:block relative">
          <div className="absolute inset-0 opacity-70">
            <img
              src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?auto=format&fit=crop&w=1920&q=80"
              alt="Diverse professional team in a modern Indian office"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-white via-white/85 to-brand-soft/70" />
          </div>
          <div className="container-brand relative z-10 py-28 lg:py-32">
            <span className="eyebrow">About Ascend Tech Solution</span>
            <h1 className="mt-4 heading-1 max-w-4xl">
              People-First Recruitment for Modern Organizations
            </h1>
            <p className="mt-6 max-w-2xl body-lg">
              Based in Pune, Maharashtra, Ascend Tech Solution supports
              organizations and professionals through focused recruitment, talent
              sourcing and hiring coordination across IT and Non-IT functions.
            </p>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="section-pad bg-white">
        <div className="container-brand grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="eyebrow">Who We Are</span>
            <h2 className="mt-3 heading-2">
              A Recruitment Consultancy Built on Understanding
            </h2>
            <div className="mt-6 space-y-5 body-lg">
              <p>
                Ascend Tech Solution is a recruitment consultancy focused on
                connecting organizational requirements with relevant
                professional talent. We work across technology and
                non-technology functions, supporting employers with structured
                hiring assistance while maintaining clear and respectful
                communication with candidates.
              </p>
              <p>
                We believe strong recruitment begins with understanding. By
                learning about the role, required capabilities and business
                context, we aim to make every search more focused and
                meaningful.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?auto=format&fit=crop&w=800&q=80"
                alt="Recruiter interacting with a candidate"
                className="w-full h-56 sm:h-72 object-cover rounded-2xl"
                loading="lazy"
              />
              <img
                src="https://images.pexels.com/photos/7255432/pexels-photo-7255432.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="HR consultation meeting"
                className="w-full h-56 sm:h-72 object-cover rounded-2xl mt-8"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VISION + MISSION */}
      <section className="section-pad bg-brand-soft">
        <div className="container-brand grid md:grid-cols-2 gap-6 lg:gap-8">
          <div className="card-surface overflow-hidden">
            <div className="relative h-52">
              <img
                src="https://images.pexels.com/photos/7644081/pexels-photo-7644081.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Team collaboration and leadership discussion"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-royal/80 to-transparent" />
              <span className="absolute bottom-4 left-5 text-white font-heading font-bold text-xl">
                Our Vision
              </span>
            </div>
            <div className="p-6 md:p-8">
              <p className="body-lg">
                To become a trusted recruitment partner known for professional
                communication, relevant talent connections and a people-centered
                approach to hiring.
              </p>
            </div>
          </div>

          <div className="card-surface overflow-hidden">
            <div className="relative h-52">
              <img
                src="https://images.pexels.com/photos/7255432/pexels-photo-7255432.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Recruiter helping a professional in an interview"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-teal/80 to-transparent" />
              <span className="absolute bottom-4 left-5 text-white font-heading font-bold text-xl">
                Our Mission
              </span>
            </div>
            <div className="p-6 md:p-8">
              <p className="body-lg">
                To support organizations with responsive and structured
                recruitment solutions while helping professionals discover
                opportunities aligned with their capabilities and aspirations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="section-pad bg-white">
        <div className="container-brand">
          <div className="max-w-2xl">
            <span className="eyebrow">Core Values</span>
            <h2 className="mt-3 heading-2">Principles That Guide Our Work</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {VALUES.map((v, idx) => (
              <div
                key={v.title}
                className={`card-surface p-6 ${
                  idx === 2 ? "sm:col-span-2 lg:col-span-1" : ""
                }`}
              >
                <div className="p-2.5 rounded-lg bg-brand-soft text-brand-royal w-fit">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading font-bold text-brand-navy">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-brand-textSoft leading-relaxed">
                  {v.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="section-pad bg-brand-soft">
        <div className="container-brand grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="eyebrow">Our Approach</span>
            <h2 className="mt-3 heading-2">How We Approach Recruitment</h2>
            <p className="mt-5 body-lg">
              A simple, repeatable rhythm that keeps hiring conversations
              focused and honest — from the first brief to the final follow-up.
            </p>
            <div className="mt-8 rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1758518729685-f88df7890776?auto=format&fit=crop&w=1200&q=80"
                alt="Recruiter listening to a candidate"
                className="w-full h-72 object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {APPROACH.map((a) => (
              <div key={a.step} className="card-surface p-6">
                <div className="flex items-center gap-3">
                  <span className="font-heading font-extrabold text-brand-teal text-sm tracking-widest">
                    {a.step}
                  </span>
                  <span className="h-px flex-1 bg-brand-border" />
                  <div className="p-2 rounded-lg bg-brand-royal/10 text-brand-royal">
                    <a.icon className="h-4 w-4" />
                  </div>
                </div>
                <h3 className="mt-4 font-heading font-bold text-brand-navy text-lg">
                  {a.title}
                </h3>
                <p className="mt-2 text-sm text-brand-textSoft leading-relaxed">
                  {a.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="section-pad bg-white">
        <div className="container-brand grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1769556353286-b7f50da70d0c?auto=format&fit=crop&w=1200&q=80"
                alt="Modern Pune business environment"
                className="w-full h-[380px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="eyebrow">Location</span>
            <h2 className="mt-3 heading-2">Rooted in Pune. Focused on Talent.</h2>
            <p className="mt-5 body-lg">
              Ascend Tech Solution is based in Pune, Maharashtra, one of
              India&rsquo;s leading centers for technology, business services
              and professional talent.
            </p>
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-brand-border bg-brand-soft p-5">
              <MapPin className="h-5 w-5 text-brand-royal mt-0.5 shrink-0" />
              <div>
                <div className="font-heading font-bold text-brand-navy">
                  Pune, Maharashtra, India
                </div>
                <div className="text-sm text-brand-textSoft mt-1">
                  Serving employers and professionals across India.
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/contact" className="btn-primary">
                Get in Touch <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
